import React, { useCallback, useContext, useState } from 'react';
import TabIntro from '../../Common/Form/Tab/TabIntro';
import LastUpdated from '../LastUpdated';
import { UserContext } from '../UserContext';
import { Button, Checkbox, Radio } from '../../Common/Form';
import { TabContext } from '../../Common/Form/Tab/TabContext';
import Feedback from '../Feedback';
import useMainContent from '../../../hooks/useMainContent';

const YourPreferencesForm = () => {
    const { user, topics } = useContext(UserContext);
    const { tabTouched, setTabTouched, mustSave } = useContext(TabContext);

    const [interests, setInterests] = useState(null);
    const [inPersonEvents, setInPersonEvents] = useState(false);
    const [virtualEvents, setVirtualEvents] = useState(false);
    const [globalError, setGlobalError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (interests === null && user !== null) {
        setInterests(user.interests.filter((t) => t.level === 1).map((t) => t.id));
        setInPersonEvents(user.eventsInPerson);
        setVirtualEvents(user.eventsVirtual);
    }

    const selectInterest = useCallback(
        (id) => {
            const method = interests.includes(id) ? 'difference' : 'union';
            setInterests(Array.from(new Set(interests)[method](new Set([id]))));
        },
        [interests]
    );

    const selectAllInterests = useCallback(
        (e) => {
            setInterests(e.currentTarget.checked ? Object.entries(topics).map(([, t]) => parseInt(t.value)) : []);
        },
        [topics, setInterests]
    );

    const selectAllEvents = useCallback(
        (e) => {
            setInPersonEvents(e.currentTarget.checked);
            setVirtualEvents(e.currentTarget.checked);
        },
        [setInPersonEvents, setVirtualEvents]
    );

    const scrollToMainContent = useMainContent();

    const onSubmit = useCallback(
        (e) => {
            e.preventDefault();
            setIsSubmitting(true);

            const data = {
                'fields[eventsInPerson]': +inPersonEvents,
                'fields[eventsVirtual]': +virtualEvents,
                userId: user.userId,
                action: 'users/save-user',
            };

            let count = 0;

            Object.values(topics).forEach((topic) => {
                if (interests.includes(topic.value)) {
                    [topic.value, ...topic.children].forEach((child) => {
                        // eslint-disable-next-line no-plusplus
                        data[`fields[memberInterests][${count++}]`] = child;
                    });
                }
            });

            if (count === 0) {
                data['fields[memberInterests]'] = '';
            }

            fetch('/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-Token': window.token,
                },
                body: new URLSearchParams(data),
            })
                .then(() => {
                    setTabTouched(false);
                    setSuccess(true);
                    setIsSubmitting(false);
                })
                .catch((err) => {
                    setGlobalError(true);
                })
                .finally(() => {
                    scrollToMainContent();
                });
        },
        [interests, inPersonEvents, virtualEvents, user, setTabTouched, topics, scrollToMainContent]
    );

    if (!(user && topics && interests)) {
        return null;
    }

    return (
        <>
            <TabIntro text="By selecting topics below, you can customize the events and insights that will be highlighted in your dashboard.">
                <LastUpdated date={user?.dateUpdated} />
            </TabIntro>

            <Feedback
                errors={{}}
                success={success}
                globalError={globalError}
                mustSave={mustSave}
                setSuccess={setSuccess}
            />

            <form className="form" onInput={() => !tabTouched && setTabTouched(true)} onSubmit={onSubmit}>
                <div>
                    <div className="grid -third -form">
                        <div className="grid__cell">
                            <Radio
                                id="all-topics"
                                label="All Topics"
                                onChange={selectAllInterests}
                                checked={(interests ?? []).length === Object.keys(topics).length}
                            />
                        </div>
                    </div>
                </div>

                {Object.entries(topics).map(([name, topic]) => (
                    <div key={`topic-${topic.value}`}>
                        <div className="grid -third -form">
                            <div className="grid__cell">
                                <Checkbox
                                    id={`topic-${topic.value}`}
                                    label={name}
                                    onChange={() => selectInterest(topic.value)}
                                    checked={(interests ?? []).includes(topic.value)}
                                />
                            </div>
                            <div className="grid__cell">{topic.description}</div>
                        </div>
                    </div>
                ))}

                <hr />

                <div>
                    <div className="grid -third -form">
                        <div className="grid__cell">
                            <Radio
                                id="all-events"
                                label="All Events"
                                onChange={selectAllEvents}
                                checked={!!inPersonEvents && !!virtualEvents}
                            />
                        </div>
                    </div>

                    <div>
                        <div className="grid -third -form">
                            <div className="grid__cell">
                                <Checkbox
                                    id="in-person-events"
                                    label="In Person"
                                    onChange={() => setInPersonEvents(!inPersonEvents)}
                                    checked={!!inPersonEvents}
                                />
                            </div>
                            <div className="grid__cell">Focus roundtables, regional day events</div>
                        </div>
                    </div>

                    <div>
                        <div className="grid -third -form">
                            <div className="grid__cell">
                                <Checkbox
                                    id="virtual-events"
                                    label="Virtual"
                                    onChange={() => setVirtualEvents(!virtualEvents)}
                                    checked={!!virtualEvents}
                                />
                            </div>
                            <div className="grid__cell">CutterCast webinars, Vendor Views, Virtual roundtables</div>
                        </div>
                    </div>
                </div>

                <div className="form__actions">
                    <Button text="Update My Preferences" className="-cta" disabled={isSubmitting} />
                </div>
            </form>
        </>
    );
};

YourPreferencesForm.propTypes = {};

export default YourPreferencesForm;
